/**
 * Dataset Items API Service
 *
 * Handles all dataset item-related API calls for flexible content types
 * (documents, qa_pairs, structured data)
 */

import { AI_OPERATIONS_API_URL } from './config';
import { demoModeManager } from './demoModeManager';

const API_BASE_URL = `${AI_OPERATIONS_API_URL}/genaiops`;
const isDemoActive = (): boolean => demoModeManager.getDemoMode().isDemoMode;

/**
 * Get the selected client ID from localStorage
 */
const getSelectedClientId = (): string | null => {
  return localStorage.getItem('selectedClientId');
};

/**
 * Append client_id to URL if available
 */
const appendClientId = (url: string): string => {
  const clientId = getSelectedClientId();
  if (!clientId) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}client_id=${clientId}`;
};

/**
 * Get authorization headers with JWT token
 */
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

export interface ListDatasetItemsParams {
  page?: number;
  limit?: number;
  itemType?: string;
  search?: string;
}

/**
 * List dataset items with pagination and filtering
 */
export const listDatasetItems = async (datasetId: string, params: ListDatasetItemsParams = {}): Promise<any> => {
  if (isDemoActive()) return { data: [], meta: { page: 1, limit: 50, total: 0, totalPages: 1 } };
  const { page = 1, limit = 50, itemType = '', search = '' } = params;

  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...(itemType && { item_type: itemType }),
    ...(search && { search }),
  });

  const response = await fetch(
    appendClientId(`${API_BASE_URL}/datasets/${datasetId}/items?${queryParams}`),
    { headers: getAuthHeaders() }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch dataset items: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get a single dataset item by ID
 */
export const getDatasetItem = async (datasetId: string, itemId: string): Promise<any> => {
  if (isDemoActive()) return { id: itemId, dataset_id: datasetId, content: {} };
  const response = await fetch(
    appendClientId(`${API_BASE_URL}/datasets/${datasetId}/items/${itemId}`),
    { headers: getAuthHeaders() }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch dataset item: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Create a new dataset item
 */
export const createDatasetItem = async (datasetId: string, itemData: Record<string, any>): Promise<any> => {
  if (isDemoActive()) return { id: 'demo-item-' + Date.now(), dataset_id: datasetId, ...itemData };
  const response = await fetch(
    appendClientId(`${API_BASE_URL}/datasets/${datasetId}/items`),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(itemData),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to create dataset item: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Create multiple dataset items at once
 */
export const bulkCreateDatasetItems = async (datasetId: string, items: Record<string, any>[]): Promise<any> => {
  if (isDemoActive()) return { created: items.length, items: items.map((item, i) => ({ id: `demo-item-bulk-${Date.now()}-${i}`, dataset_id: datasetId, ...item })) };
  const response = await fetch(
    appendClientId(`${API_BASE_URL}/datasets/${datasetId}/items/bulk`),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ items }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to create dataset items: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Update an existing dataset item
 */
export const updateDatasetItem = async (datasetId: string, itemId: string, updates: Record<string, any>): Promise<any> => {
  if (isDemoActive()) return { id: itemId, dataset_id: datasetId, ...updates };
  const response = await fetch(
    appendClientId(`${API_BASE_URL}/datasets/${datasetId}/items/${itemId}`),
    {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to update dataset item: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Delete a dataset item (soft delete)
 */
export const deleteDatasetItem = async (datasetId: string, itemId: string): Promise<any> => {
  if (isDemoActive()) return { success: true };
  const response = await fetch(
    appendClientId(`${API_BASE_URL}/datasets/${datasetId}/items/${itemId}`),
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to delete dataset item: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Bulk delete dataset items
 */
export const bulkDeleteDatasetItems = async (datasetId: string, itemIds: string[]): Promise<any> => {
  if (isDemoActive()) return { deleted: itemIds.length };
  const response = await fetch(
    appendClientId(`${API_BASE_URL}/datasets/${datasetId}/items/bulk`),
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ item_ids: itemIds }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to delete dataset items: ${response.statusText}`);
  }

  return response.json();
};
